/**
 * 🍳 Dave's Diner — David Lauer's overwhelmed eatery. Host of the onboarding
 * quest climax. Dave reads Cari's family journal, recognizes the Lv 1 recipe,
 * spawns the sub-quest (basic egg + bread), then welcomes the hero in after
 * they return with the goods.
 *
 * After onboarding completion, this page becomes the food-features hub
 * (Kitchen link, Nest egg sources, Diner specials).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_DAVE_PROMPTS = {
  // Before the journal arrives — Dave doesn't know who you are
  none: {
    icon: "🍳",
    title: "Dave hasn't met you yet.",
    body: (
      <>
        The diner is closed. <i>"BACK IN 5"</i> sign is taped to the door, but
        Dave's been "back in 5" for what looks like several weeks.
        <br /><br />
        Maybe ask General Cari at the Library about the kingdom first.
      </>
    ),
    next: { label: "📚 Go to the Library", to: "/library" },
  },
  cari_lecture_pending: {
    icon: "🍳",
    title: "Dave hasn't met you yet.",
    body: (
      <>
        The diner is closed. Cari at the Library wanted a word with you first —
        something about hiding your Chits. Go see her.
      </>
    ),
    next: { label: "📚 Go to the Library", to: "/library" },
  },
  // You have the journal — Dave is in here, overwhelmed, hasn't read it
  asking_around: {
    icon: "🍳",
    title: "The door creaks open. Dave looks up — and freezes.",
    body: null, // we'll render the NPC dialog block dynamically
    showJournalCTA: true,
  },
  // Dave's seen the journal — sub-quest is active
  dave_overwhelmed: {
    icon: "🥚🍞",
    title: "Dave is panicking. He needs ingredients.",
    body: null,
    showSubQuestBoard: true,
  },
  gathering: {
    icon: "🥚🍞",
    title: "You've got one — keep going.",
    body: null,
    showSubQuestBoard: true,
  },
  completed: {
    icon: "🍳",
    title: "Welcome to Phoenix Flame Diner!",
    body: null,
    showOpenAchievement: true,
  },
};

export default function DinerPage() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [npcResponse, setNpcResponse] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/onboarding/state`);
      setState(r.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Diner is closed.");
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    refresh();
  }, [isAuthed]);

  const askDave = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/ask-npc`, { npc_id: "dave" });
      setNpcResponse(r.data.npc);
      setState(r.data.state);
      window.dispatchEvent(new Event("omnimuscle:onboarding-poke"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dave's too busy right now.");
    } finally {
      setBusy(false);
    }
  };

  const turnIn = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/dave/turn-in`);
      setState(r.data);
      setNpcResponse(null);
      toast.success("🍳 Dave taught you Eggs & Toast! Kitchen unlocked.");
      window.dispatchEvent(new Event("omnimuscle:onboarding-poke"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dave couldn't accept yet.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">🐦‍🔥</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            PHOENIX FLAME DINER
          </h1>
          <p className="text-neutral-400">Sign in to meet Dave.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const stage = state?.stage || "none";
  const prompt = STAGE_DAVE_PROMPTS[stage] || STAGE_DAVE_PROMPTS.none;
  const inv = state?.sub_quest_inventory || {};
  const hasEgg = (inv.basic_egg || 0) >= 1;
  const hasBread = (inv.loaf_of_bread || 0) >= 1;
  const canTurnIn = hasEgg && hasBread;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100" data-testid="diner-page">
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-6 pb-24">
        {/* Hero */}
        <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a0a00] via-black/60 to-[#0a0500] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">🐦‍🔥</div>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.4em] text-amber-300/70 mb-1">Kingdom of Dragonious</div>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🐦‍🔥 PHOENIX FLAME DINER
            </h1>
            <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">
              Owner: <b>David "Dave" Lauer</b> · overwhelmed cook · Nest connoisseur · the only one in
              town who reads dusty cookbooks fluently. Lvl 1 recipes start here.
            </p>
          </div>
        </div>

        {/* The scene */}
        <div className="rounded-2xl border-2 border-purple-700/50 bg-gradient-to-br from-[#10001a] to-black/60 p-6 mb-6">
          <div className="flex items-start gap-5">
            <div className="text-6xl flex-shrink-0">{prompt.icon}</div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-2xl font-black text-amber-200 mb-3"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="diner-scene-title"
              >
                {prompt.title}
              </h2>

              {prompt.body && (
                <div className="text-base text-amber-100/90 leading-relaxed font-serif">
                  {prompt.body}
                </div>
              )}

              {prompt.showJournalCTA && (
                <JournalHandoffBlock
                  journal={state?.journal}
                  npcResponse={npcResponse}
                  busy={busy}
                  onAsk={askDave}
                />
              )}

              {prompt.showSubQuestBoard && (
                <SubQuestBoard
                  state={state}
                  hasEgg={hasEgg}
                  hasBread={hasBread}
                  canTurnIn={canTurnIn}
                  busy={busy}
                  onTurnIn={turnIn}
                />
              )}

              {prompt.showOpenAchievement && (
                <DinerOpenAchievement completedAt={state?.completed_at} />
              )}

              {prompt.next && (
                <div className="mt-5">
                  <Link
                    to={prompt.next.to}
                    data-testid="diner-next-link"
                    className="inline-block px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm"
                  >
                    {prompt.next.label}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Post-onboarding Diner hub */}
        {stage === "completed" && <DinerHub />}
      </main>

      <Footer />
    </div>
  );
}


function JournalHandoffBlock({ journal, npcResponse, busy, onAsk }) {
  if (!journal) {
    return (
      <div className="mt-4 text-sm text-neutral-400 italic">
        You don't have the journal yet. Go see Cari at the 📚 Library.
      </div>
    );
  }
  if (!npcResponse) {
    return (
      <div className="mt-4">
        <div className="rounded-xl border border-amber-700/40 bg-amber-900/10 p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="text-3xl">{journal.icon}</div>
            <div>
              <div className="text-sm font-black text-amber-200">{journal.name}</div>
              <div className="text-[11px] text-neutral-500 italic">{journal.lore}</div>
            </div>
          </div>
        </div>
        <button
          onClick={onAsk}
          disabled={busy}
          data-testid="show-dave-journal-btn"
          className="px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-sm"
        >
          📜 Show Dave the Journal
        </button>
      </div>
    );
  }
  return <NpcDialogBlock npc={npcResponse} />;
}


function NpcDialogBlock({ npc }) {
  return (
    <div className="mt-4 rounded-xl border-2 border-amber-700/50 bg-gradient-to-br from-amber-900/10 to-black/40 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-4xl">{npc.icon}</div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300/70">{npc.location}</div>
          <div className="text-base font-black text-amber-200">{npc.name}</div>
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-purple-300/70 mb-2">{npc.title}</div>
      <div className="space-y-3 text-base text-amber-100/95 leading-relaxed font-serif">
        {npc.lines.map((line, i) => (
          <p key={i} data-testid={`npc-line-${i}`}>{line}</p>
        ))}
      </div>
    </div>
  );
}


function SubQuestBoard({ state, hasEgg, hasBread, canTurnIn, busy, onTurnIn }) {
  return (
    <div className="mt-4">
      <div className="rounded-xl border-2 border-amber-700/60 bg-gradient-to-br from-amber-900/15 to-black/40 p-5 mb-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300/70 mb-1">📜 Sub-Quest</div>
        <div className="text-lg font-black text-amber-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
          Help Dave Find Ingredients
        </div>
        <div className="text-sm text-amber-100/85 mb-4 leading-relaxed font-serif">
          Dave's bread box is empty. His mini-fridge is empty. He can't cook the
          journal's Lv 1 recipe without help.
        </div>
        <div className="text-[11px] text-neutral-400 italic mb-4">
          💡 Hint: {state?.sub_quest_hint}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <IngredientCard
            label="Basic Egg"
            icon="🥚"
            have={hasEgg}
            hint="Lay one at your Nest 🪹 — your Phoenix produces these naturally."
            cta={{ to: "/nest", label: "🪹 Visit Nest" }}
          />
          <IngredientCard
            label="Loaf of Bread"
            icon="🍞"
            have={hasBread}
            hint="Old Cari might have one — she's always feeding the birds at the Library."
            cta={{ to: "/library", label: "📚 Ask Cari" }}
          />
        </div>
      </div>

      <button
        onClick={onTurnIn}
        disabled={busy || !canTurnIn}
        data-testid="diner-turn-in-btn"
        className={`w-full px-5 py-3 rounded font-black text-sm tracking-wide ${
          canTurnIn
            ? "bg-amber-500 hover:bg-amber-400 text-black shadow-[0_0_24px_rgba(245,158,11,0.4)]"
            : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
        }`}
      >
        {canTurnIn
          ? "🍳 Hand Dave the ingredients →"
          : `Bring back: ${!hasEgg ? "🥚 egg" : ""} ${!hasBread ? "🍞 bread" : ""}`.trim()}
      </button>
    </div>
  );
}


function IngredientCard({ label, icon, have, hint, cta }) {
  return (
    <div
      data-testid={`ingredient-card-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`rounded-lg border p-3 transition ${
        have
          ? "border-amber-500 bg-amber-500/10 shadow-[0_0_18px_rgba(245,158,11,0.18)]"
          : "border-purple-700/40 bg-black/30"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-3xl">{icon}</div>
        {have ? (
          <span className="px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-black">✔ HAVE</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400 text-[10px] font-black">NEED</span>
        )}
      </div>
      <div className="text-sm font-black text-amber-100">{label}</div>
      <div className="text-[11px] text-neutral-400 italic mt-1">{hint}</div>
      {!have && (
        <Link
          to={cta.to}
          className="inline-block mt-2 text-[11px] font-bold text-amber-300 hover:text-amber-200 underline"
        >
          {cta.label}
        </Link>
      )}
    </div>
  );
}


function DinerHub() {
  return (
    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="diner-hub-grid">
      {[
        { to: "/kitchen", icon: "🍳", title: "The Kitchen",
          desc: "Cook the recipes you've discovered." },
        { to: "/blue-mage-menu", icon: "💙", title: "Blue Mage Menu",
          desc: "Vesper's chalk menu — learn skills by eating monster ingredients.",
          testid: "diner-blue-mage-card" },
        { to: "/nest", icon: "🪹", title: "Phoenix Nest",
          desc: "Where eggs come from. Your pet lays them naturally." },
        { to: "/library", icon: "📚", title: "Cari's Library",
          desc: "Lore, tomes, and the occasional loaf of bread." },
      ].map((h) => (
        <Link
          key={h.to}
          to={h.to}
          data-testid={h.testid}
          className="rounded-xl border border-purple-700/40 bg-black/40 hover:border-amber-500 hover:shadow-[0_0_20px_rgba(245,158,11,0.18)] transition p-4 block"
        >
          <div className="text-4xl mb-2">{h.icon}</div>
          <div className="text-sm font-black text-amber-200">{h.title}</div>
          <div className="text-[11px] text-neutral-400 mt-1">{h.desc}</div>
        </Link>
      ))}
    </div>
  );
}



/**
 * 🏆 "DAVE'S DINER IS OPEN" achievement card — shows when the player has
 * completed the onboarding quest chain. Designed to feel like a shareable
 * trophy: bold typography, dinerly neon glow, copy-to-clipboard share button.
 */
function DinerOpenAchievement({ completedAt }) {
  const { user } = useAuth();
  const heroName = user?.name || "Phoenix";
  const date = completedAt
    ? new Date(completedAt).toLocaleDateString(undefined,
        { year: "numeric", month: "short", day: "numeric" })
    : "today";

  const shareText =
    `🐦‍🔥 Phoenix Flame Diner is OPEN!\n\n` +
    `${heroName} just finished the onboarding quest in Dragonious — ` +
    `dragged a basic egg 🥚 and a loaf of bread 🍞 across town to a panicking cook ` +
    `and walked out with the Lv 1 recipe: Eggs & Toast.\n\n` +
    `Come fight, farm, and cook with me. omni-brand-1.emergent.host #OmniMuscle`;

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, title: "Phoenix Flame Diner is OPEN!" });
        toast.success("Shared!");
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("📋 Copied to clipboard — paste it anywhere!");
    } catch {
      toast.error("Couldn't copy automatically.");
    }
  };

  return (
    <div
      data-testid="diner-open-achievement"
      className="mt-4 relative rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-900/30 via-[#1a0a00] to-amber-900/15 p-6 shadow-[0_0_40px_rgba(245,158,11,0.35)] overflow-hidden"
    >
      {/* Neon "OPEN" sign glow */}
      <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.4em] text-amber-300 font-black animate-pulse">
        ★ OPEN ★
      </div>
      <div className="absolute -bottom-6 -right-6 text-[180px] opacity-10 select-none rotate-12">
        🍳
      </div>

      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/80 mb-1">Achievement Unlocked</div>
        <h2
          className="text-4xl md:text-5xl font-black text-amber-200 mb-2 leading-none"
          style={{ fontFamily: "Anton, sans-serif", textShadow: "0 0 24px rgba(245,158,11,0.5)" }}
        >
          DAVE'S DINER<br />IS OPEN
        </h2>
        <p className="text-sm text-amber-100/90 italic font-serif leading-relaxed max-w-xl">
          Dave wipes his hands on his apron and grins. The bread box is full again.
          The mini-fridge is stocked. The neon sign hums to life. He pours you a
          cup of coffee on the house. "You're a real one, kid."
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
          <div className="rounded-lg border border-amber-700/50 bg-black/40 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-purple-300/70">Recipe Learned</div>
            <div className="text-amber-200 font-black">🍳 Eggs & Toast (Lv 1)</div>
          </div>
          <div className="rounded-lg border border-amber-700/50 bg-black/40 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-purple-300/70">Hero</div>
            <div className="text-amber-200 font-black">{heroName}</div>
          </div>
          <div className="rounded-lg border border-amber-700/50 bg-black/40 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-purple-300/70">Completed</div>
            <div className="text-amber-200 font-black">{date}</div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={onShare}
            data-testid="diner-share-btn"
            className="px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-black text-sm tracking-wide"
          >
            📤 Share the Moment
          </button>
          <Link
            to="/kitchen"
            data-testid="diner-open-kitchen-link"
            className="px-5 py-2 rounded bg-purple-700 hover:bg-purple-600 text-white font-bold text-sm"
          >
            🍳 Open the Kitchen →
          </Link>
        </div>
      </div>
    </div>
  );
}
